/**
 * EngagementMessageModal Component - Admin-controlled user messaging
 *
 * Displays modal messages to users based on admin configuration.
 * Supports:
 * - Text and markdown content
 * - Video embeds (YouTube/Vimeo)
 * - Configurable actions (dismiss, navigate, external link)
 * - "Don't show again" option
 * - A centered pop-up that fills most of the window
 */

import React, { useEffect, useRef } from 'react';
import { useEngagementMessage } from '../../context/EngagementMessageContext';
import { isExplainerMessage } from '../../utils/engagementShow';
import MediaEmbed from './MediaEmbed';
import MarkdownContent from './MarkdownContent';
import MaintenanceCountdown from './MaintenanceCountdown';
import './EngagementMessageModal.css';

function EngagementMessageModal() {
  const {
    pendingMessage,
    isVisible,
    dismissMessage,
    dontShowAgain,
    setDontShowAgain
  } = useEngagementMessage();

  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Focus management - trap focus within modal
  useEffect(() => {
    if (isVisible && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isVisible]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isVisible && pendingMessage) {
        dismissMessage(pendingMessage.id, dontShowAgain, 'dismiss');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, pendingMessage, dontShowAgain, dismissMessage]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  if (!isVisible || !pendingMessage) return null;

  const handleDismiss = () => {
    dismissMessage(pendingMessage.id, dontShowAgain, 'dismiss');
  };

  const handleAction = () => {
    const actionType = pendingMessage.primary_action_type;

    if (actionType === 'navigate') {
      dismissMessage(pendingMessage.id, dontShowAgain, 'navigate');
      // Use window.location for full page navigation
      window.location.href = pendingMessage.primary_action_url;
    } else if (actionType === 'external') {
      // Open in new tab, then dismiss
      window.open(pendingMessage.primary_action_url, '_blank');
      dismissMessage(pendingMessage.id, dontShowAgain, 'external');
    } else {
      // Default: just dismiss
      dismissMessage(pendingMessage.id, dontShowAgain, 'dismiss');
    }
  };

  const modalSizeClass = pendingMessage.modal_size === 'compact'
    ? 'engagement-modal--compact'
    : 'engagement-modal--standard';

  return (
    <div className="engagement-modal-overlay">
      <div
        ref={modalRef}
        className={`engagement-modal ${modalSizeClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="engagement-modal-title"
      >
        <div className="engagement-modal__header">
          <h2 id="engagement-modal-title" className="engagement-modal__title">
            {pendingMessage.title}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            className="engagement-modal__close"
            onClick={handleDismiss}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="engagement-modal__body">
          {/* Show countdown for maintenance messages */}
          {pendingMessage.message_type === 'maintenance' && pendingMessage.maintenance_time && (
            <MaintenanceCountdown targetTime={pendingMessage.maintenance_time} />
          )}

          {pendingMessage.content_type === 'media' && pendingMessage.media_url && (
            <MediaEmbed
              url={pendingMessage.media_url}
              thumbnail={pendingMessage.media_thumbnail_url}
            />
          )}
          <div className="engagement-modal__content">
            <MarkdownContent content={pendingMessage.content} />
          </div>
        </div>

        <div className="engagement-modal__footer">
          {pendingMessage.show_dont_show_again && !isExplainerMessage(pendingMessage) && (
            <label className="engagement-modal__checkbox">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
              <span>Don't show this again</span>
            </label>
          )}
          <button
            type="button"
            className="engagement-modal__action"
            onClick={handleAction}
          >
            {pendingMessage.primary_action_label || 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EngagementMessageModal;
